/**
 * VideoStrip — saved video clips for one checklist item.
 * Chips with duration; tap Play → fetches the clip (auth) into a modal player.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtClipDuration } from "@/lib/videoUpload";
import { useOverlayBackClose } from "@/hooks/backGuard";
import { Play, Trash2, Loader2, Video, X } from "lucide-react";
import { toast } from "sonner";

const VideoStrip = ({ inspectionId, itemId, refreshKey, testidPrefix }) => {
  const [videos, setVideos] = useState([]);
  const [playing, setPlaying] = useState(null); // { ...video, url }
  const [loadingId, setLoadingId] = useState("");

  useEffect(() => {
    if (!inspectionId) return;
    api.get(`/inspections/${inspectionId}/videos`, { params: { item_id: itemId } })
      .then((r) => setVideos(r.data || []))
      .catch(() => {});
  }, [inspectionId, itemId, refreshKey]);

  const play = async (v) => {
    setLoadingId(v.id);
    try {
      const r = await api.get(`/inspections/${inspectionId}/videos/${v.id}/stream`, { responseType: "blob" });
      setPlaying({ ...v, url: URL.createObjectURL(r.data) });
    } catch {
      toast.error("Could not load the video — check your connection");
    } finally {
      setLoadingId("");
    }
  };

  const closePlayer = () => {
    if (playing) URL.revokeObjectURL(playing.url);
    setPlaying(null);
  };

  const del = async (v) => {
    if (!window.confirm("Delete this video? This can't be undone.")) return;
    try {
      await api.delete(`/inspections/${inspectionId}/videos/${v.id}`);
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
      toast.success("Video deleted");
    } catch {
      toast.error("Could not delete the video");
    }
  };

  useOverlayBackClose(!!playing, closePlayer);

  if (!videos.length) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testidPrefix}-video-strip`}>
      {videos.map((v) => (
        <div key={v.id} className="inline-flex items-center gap-2 border-2 border-slate-300 bg-slate-50 pl-2.5 pr-1.5 py-1.5"
          data-testid={`${testidPrefix}-video-chip-${v.id}`}>
          <Video size={14} className="text-[#1E1B4B] shrink-0" strokeWidth={2.5}/>
          <span className="text-xs font-bold text-slate-800">{fmtClipDuration(v.duration_seconds || 0)}</span>
          <button type="button" onClick={() => play(v)} disabled={loadingId === v.id}
            data-testid={`${testidPrefix}-video-play-${v.id}`}
            className="h-8 px-2.5 inline-flex items-center gap-1 bg-[#1E1B4B] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#0F0E2E] disabled:opacity-60">
            {loadingId === v.id ? <Loader2 size={11} className="animate-spin"/> : <Play size={11} strokeWidth={3}/>}
            Play
          </button>
          <button type="button" onClick={() => del(v)}
            data-testid={`${testidPrefix}-video-delete-${v.id}`}
            className="h-8 w-8 inline-flex items-center justify-center text-red-600 hover:bg-red-50">
            <Trash2 size={13} strokeWidth={2.5}/>
          </button>
        </div>
      ))}

      {playing && (
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col" data-testid="video-player-modal">
          <div className="shrink-0 h-16 px-4 flex items-center justify-between bg-black/80 text-white">
            <span className="text-sm font-black uppercase tracking-wider truncate">
              {playing.filename} · {fmtClipDuration(playing.duration_seconds || 0)}
            </span>
            <button onClick={closePlayer} data-testid="video-player-close"
              className="h-11 w-11 inline-flex items-center justify-center"><X size={20}/></button>
          </div>
          <video src={playing.url} controls autoPlay playsInline className="flex-1 min-h-0 w-full object-contain bg-black"/>
        </div>
      )}
    </div>
  );
};

export default VideoStrip;
